/**
 * Editor Configuration Hook
 * 
 * Manages Syncfusion DocumentEditorContainer configuration:
 * - File menu items (declarative via props)
 * - Ribbon tab visibility (MutationObserver-based persistence)
 * - Custom buttons in Review tab
 * - Readonly mode
 */

import { useCallback, useEffect, useRef, useMemo } from 'react';
import type { DocumentEditorContainerComponent, FileMenuItemType } from '@syncfusion/ej2-react-documenteditor';
import type { MenuItemModel } from '@syncfusion/ej2-navigations';
import type {
  EditorConfig,
  RibbonTabVisibility,
} from '../types/editor.types';
import {
  buildFileMenuItems,
  getRibbonTabVisibility,
} from '../types/editor.types';

// =============================================================================
// CONSTANTS
// =============================================================================

const REVIEW_TAB_ID = 'Review';
const DEVELOPER_TAB_ID = 'Developer';
const EDITABLE_REGIONS_GROUP_ID = 'editableRegionsGroup';

// Debounce delay for MutationObserver callbacks (ms)
const MUTATION_DEBOUNCE_MS = 50;

// =============================================================================
// TYPES
// =============================================================================

export interface UseEditorConfigurationProps {
  /** Editor container ref */
  editorRef: React.RefObject<DocumentEditorContainerComponent | null>;
  /** Current editor configuration (null if not yet received) */
  config: EditorConfig | null;
  /** Callback when configuration is fully applied */
  onConfigApplied?: () => void;
  /** Callback when custom tab buttons are clicked */
  onAddEditableRegion?: () => void;
  onClearEditableRegions?: () => void;
}

export interface UseEditorConfigurationReturn {
  /** File menu items to pass to DocumentEditorContainer */
  fileMenuItems: (FileMenuItemType | MenuItemModel)[];
  /** Handler for Syncfusion created event */
  handleEditorCreated: () => void;
  /** Handler for file menu item click */
  handleFileMenuClick: (args: { item: { id: string } }) => void;
  /** Whether configuration has been applied */
  isConfigured: boolean;
  /** Apply configuration manually (for reinitialize scenarios) */
  applyConfiguration: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

export function useEditorConfiguration({
  editorRef,
  config,
  onConfigApplied,
  onAddEditableRegion,
  onClearEditableRegions,
}: UseEditorConfigurationProps): UseEditorConfigurationReturn {
  // Track configuration state
  const isConfiguredRef = useRef(false);
  const customButtonsAddedRef = useRef(false);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Track which config we've already applied (by metadata.id or a hash)
  const appliedConfigIdRef = useRef<string | null>(null);
  const hasNotifiedParentRef = useRef(false);

  // ===========================================================================
  // FILE MENU ITEMS (Declarative)
  // ===========================================================================

  const fileMenuItems = useMemo(() => {
    if (!config) {
      // Default minimal menu when no config
      return ['Print'] as (FileMenuItemType | MenuItemModel)[];
    }

    const items = buildFileMenuItems(config);
    
    // Convert to Syncfusion format
    return items.map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      return {
        text: item.text,
        id: item.id,
        iconCss: item.iconCss,
        disabled: item.disabled,
      } as MenuItemModel;
    });
  }, [config]);

  // ===========================================================================
  // RIBBON TAB VISIBILITY
  // ===========================================================================

  /**
   * Get current ribbon tab visibility settings
   */
  const getRibbonVisibility = useCallback((): RibbonTabVisibility => {
    if (!config) {
      return { developer: false, masterCopy: false };
    }
    return getRibbonTabVisibility(config.documentType);
  }, [config]);

  /**
   * Apply ribbon tab visibility and hide Protect Document button
   * Called initially and on every MutationObserver callback
   */
  const applyRibbonVisibility = useCallback(() => {
    const container = editorRef.current;
    if (!container?.ribbon) {
      return;
    }

    try {
      // Always hide Developer tab
      container.ribbon.showTab(DEVELOPER_TAB_ID, false);

      // Hide Protect Document button in Review tab
      const protectDocumentBtn = container.element?.querySelector('#document-editor_ribbon_protect_document');
      if (protectDocumentBtn) {
        (protectDocumentBtn as HTMLElement).style.display = 'none';
      }
    } catch (error) {
      console.warn('[EditorConfig] Failed to apply ribbon visibility:', error);
    }
  }, [editorRef]);

  /**
   * Add custom buttons to Review tab
   * Only called once per editor instance and only for master copy documents
   */
  const addCustomButtonsToReviewTab = useCallback(() => {
    const container = editorRef.current;
    if (!container?.ribbon || customButtonsAddedRef.current || !config) {
      return;
    }
  
    const visibility = getRibbonVisibility();
    
    if (!visibility.masterCopy) {
      return;
    }
  
    try {
      const editableRegionsGroup = {
        id: EDITABLE_REGIONS_GROUP_ID,
        header: 'Editable Regions',
        collections: [
          {
            items: [
              {
                id: 'addEditableRegion',
                type: 'Button',
                buttonSettings: {
                  content: 'Add Editable Region',
                  iconCss: 'e-icons e-de-ctnr-lock',
                  clicked: () => {
                    const editor = container.documentEditor;
                    if (editor) {
                      // MUST enable highlighting BEFORE inserting region
                      editor.documentEditorSettings.highlightEditableRanges = true;
                      (editor.editor as unknown as { insertEditingRegion: (arg: string) => void }).insertEditingRegion('Everyone');
                      
                      onAddEditableRegion?.();
                    }
                  },
                },
              },
              {
                id: 'clearEditableRegions',
                type: 'Button',
                buttonSettings: {
                  content: 'Clear All Regions',
                  iconCss: 'e-icons e-close',
                  clicked: () => {
                    const editor = container.documentEditor;
                    if (editor) {
                      (editor.editor as unknown as { removeAllEditRestrictions: () => void }).removeAllEditRestrictions();
                      
                      onClearEditableRegions?.();
                    }
                  },
                },
              },
            ],
          },
        ],
      };
  
      container.ribbon.addGroup(REVIEW_TAB_ID, editableRegionsGroup);
      customButtonsAddedRef.current = true;
      console.log('[EditorConfig] Custom buttons added to Review tab');
    } catch (error) {
      console.error('[EditorConfig] Failed to add custom buttons to Review tab:', error);
    }
  }, [editorRef, config, getRibbonVisibility, onAddEditableRegion, onClearEditableRegions]);

  // ===========================================================================
  // MUTATION OBSERVER
  // ===========================================================================

  /**
   * Setup MutationObserver to persist ribbon visibility
   * Syncfusion's internal re-renders can reset visibility
   */
  const setupMutationObserver = useCallback(() => {
    const container = editorRef.current;
    if (!container?.element || mutationObserverRef.current) {
      return;
    }

    const handleMutation = () => {
      // Debounce to avoid excessive calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        applyRibbonVisibility();
      }, MUTATION_DEBOUNCE_MS);
    };

    mutationObserverRef.current = new MutationObserver(handleMutation);

    // Observe the ribbon container specifically if possible
    const ribbonElement = container.element.querySelector('.e-ribbon');
    const observeTarget = ribbonElement || container.element;

    mutationObserverRef.current.observe(observeTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    console.log('[EditorConfig] MutationObserver setup');
  }, [editorRef, applyRibbonVisibility]);

  /**
   * Cleanup MutationObserver
   */
  const cleanupMutationObserver = useCallback(() => {
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
      mutationObserverRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  // ===========================================================================
  // READONLY MODE
  // ===========================================================================

  /**
   * Apply readonly mode based on config
   */
  const applyReadonlyMode = useCallback(() => {
    const container = editorRef.current;
    if (!container?.documentEditor || !config) {
      return;
    }

    const editorModule = container.documentEditor.editor as unknown as {
      stopProtection?: (pwd?: string) => void;
    };

    if (config.canEdit) {
      editorModule.stopProtection?.('');
    }

    container.documentEditor.isReadOnly = !config.canEdit;
    console.log('[EditorConfig] Readonly mode:', !config.canEdit);
  }, [editorRef, config]);

  // ===========================================================================
  // CONFIGURATION APPLICATION
  // ===========================================================================

  /**
   * Apply all configuration
   * Called from handleEditorCreated and can be called manually for reinitialize
   */
  const applyConfiguration = useCallback(() => {
    const container = editorRef.current;
    if (!container?.ribbon || !config) {
      console.log('[EditorConfig] Cannot apply config - container or config not ready');
      return;
    }

    // Generate a config identifier to track if we've already applied this config
    const configId = config.metadata?.id || `${config.documentType}-${config.canEdit}`;
    const isNewConfig = appliedConfigIdRef.current !== configId;

    console.log('[EditorConfig] Applying configuration:', config.documentType, 'isNew:', isNewConfig);

    // 1. Add custom buttons to Review tab (if not already added)
    addCustomButtonsToReviewTab();

    // 2. Apply ribbon visibility and hide Protect Document
    applyRibbonVisibility();

    // 3. Apply readonly mode
    applyReadonlyMode();

    // 4. Setup MutationObserver for persistence
    setupMutationObserver();

    // 5. Mark as configured
    isConfiguredRef.current = true;
    appliedConfigIdRef.current = configId;

    // 6. Notify parent ONLY if this is a new config and we haven't notified yet
    if (isNewConfig && !hasNotifiedParentRef.current) {
      hasNotifiedParentRef.current = true;
      onConfigApplied?.();
      console.log('[EditorConfig] Configuration applied successfully, notified parent');
    } else {
      console.log('[EditorConfig] Configuration re-applied (no parent notification)');
    }
  }, [
    editorRef,
    config,
    addCustomButtonsToReviewTab,
    applyRibbonVisibility,
    applyReadonlyMode,
    setupMutationObserver,
    onConfigApplied,
  ]);

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  /**
   * Handler for Syncfusion created event
   */
  const handleEditorCreated = useCallback(() => {
    console.log('[EditorConfig] Editor created event');

    // The state machine transitions back to CONFIGURING on editor (re)creation.
    // Reset tracking refs so applyConfiguration() runs the full cycle —
    // including calling onConfigApplied() — to advance the state machine from
    // CONFIGURING → READY. Without this, if config had already been applied
    // once (while in IDLE), the guard inside applyConfiguration would suppress
    // the onConfigApplied callback and the state machine would stay stuck in
    // CONFIGURING forever (blocking document load and protection application).
    appliedConfigIdRef.current = null;
    hasNotifiedParentRef.current = false;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (config) {
        applyConfiguration();
      }
    });
  }, [config, applyConfiguration]);

  /**
   * Handler for file menu item clicks
   */
  const handleFileMenuClick = useCallback(
    (args: { item: { id: string } }) => {
      // Built-in items (New, Open, Export, Print) are handled by Syncfusion
      // We only need to handle custom items (save, save_as)
      const { id } = args.item;

      if (id === 'save' || id === 'save_as') {
        // These are handled by the parent component via callbacks
        // The actual save logic is in TextEditor.tsx
        console.log('[EditorConfig] File menu click:', id);
      }
    },
    []
  );

  // ===========================================================================
  // EFFECTS
  // ===========================================================================

  /**
   * Reset notification flag when config changes (for reinitialize scenario)
   */
  useEffect(() => {
    if (config) {
      const configId = config.metadata?.id || `${config.documentType}-${config.canEdit}`;
      console.log({checkconf:config})
      // If this is a genuinely new config, reset the notification flag
      if (appliedConfigIdRef.current !== configId) {
        console.log('[EditorConfig] New config detected, resetting notification flag');
        hasNotifiedParentRef.current = false;
      }
    }
  }, [config]);

  /**
 * Apply configuration when config changes AND ribbon is ready
 * This handles the reinitialize case (Save As creating new version)
 */
useEffect(() => {
  if (!config || !editorRef.current?.ribbon) {
    return;
  }

  const configId = config.metadata?.id || `${config.documentType}-${config.canEdit}`;
  
  // Only apply if this is a new config we haven't processed
  if (appliedConfigIdRef.current !== configId) {
    console.log('[EditorConfig] New config detected, applying...', configId);
    
    // Reset custom buttons flag if document type changed
    const prevConfigType = appliedConfigIdRef.current?.split('-')[0];
    const newConfigType = config.documentType;
    
    if (prevConfigType !== newConfigType) {
      console.log('[EditorConfig] Document type changed, resetting custom buttons');
      customButtonsAddedRef.current = false;
    }
    
    // Reset notification flag for new config
    hasNotifiedParentRef.current = false;
    
    // Apply the new configuration
    applyConfiguration();
  }
}, [config, applyConfiguration, editorRef]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      cleanupMutationObserver();
      isConfiguredRef.current = false;
      customButtonsAddedRef.current = false;
      appliedConfigIdRef.current = null;
      hasNotifiedParentRef.current = false;
    };
  }, [cleanupMutationObserver]);

  // ===========================================================================
  // RETURN
  // ===========================================================================

  return {
    fileMenuItems,
    handleEditorCreated,
    handleFileMenuClick,
    isConfigured: isConfiguredRef.current,
    applyConfiguration,
  };
}